import React, {MouseEventHandler} from "react";
import {StyleFn, Theme, BaseColor} from "@core-ui/react-stitch";

import {CommonProps, OptionType, Actions} from "../types";

const defaultProps = {
  highlighted: false,
  selected: false,
};

export type OptionProps = CommonProps & {
  "aria-label"?: string;
  highlighted?: boolean;
  id: string;
  index: number;
  onClick: Actions["onChange"];
  onMouseMove: MouseEventHandler;
  option?: OptionType;
  selected?: boolean;
  children: React.ReactNode;
} & typeof defaultProps;

type OptionStyleFn<Return> = StyleFn<Partial<OptionProps>, Return>;

const getColor: OptionStyleFn<BaseColor> = ({theme, crossBrand, inverse}) => {
  if (crossBrand) return theme.color.gray80;
  return inverse ? theme.color.inverse.b1 : theme.color.gray80;
};

const getFontSize: OptionStyleFn<string> = ({theme, crossBrand}) => {
  if (crossBrand) return "1.0625rem";
  if (["br", "brfs"].includes(theme.brand)) return "1rem";
  return "1.0625rem";
};

const getBackgroundColor: OptionStyleFn<BaseColor | undefined> = ({
  theme,
  selected,
  highlighted,
  inverse,
  enabledFeatures,
}) => {
  const showPdpRedesign =
    inverse !== true &&
    (enabledFeatures?.["cat-br-redesign"] ||
      enabledFeatures?.["pdp-redesign-2022"]);
  if (showPdpRedesign && ["br", "brfs"].includes(theme.brand)) {
    if (selected) return "#CECBC3";
    if (highlighted) return "#E2E0D7";
    return "#F6F4EB";
  }

  if (selected && inverse) return theme.color.inverse.g1;
  if (highlighted && inverse) return "#403C38";
  if (selected) return theme.color.gray10;
  if (highlighted) return theme.color.gray05;

  return "unset";
};

const Option = ({
  "aria-label": ariaLabel,
  bemBuilder,
  crossBrand,
  disabled,
  getStyles,
  highlighted,
  id,
  inverse,
  index,
  onClick,
  onMouseMove,
  option,
  selected,
  children,
  enabledFeatures,
}: OptionProps): JSX.Element => {
  const optionIndex = index >= 0 ? index : -1;
  return (
    <li
      aria-label={ariaLabel}
      aria-selected={selected}
      className={bemBuilder("option", {
        "option--disabled": disabled,
        "option--highlighted": highlighted,
      })}
      css={(theme: Theme) =>
        getStyles(
          "Option",
          {
            cursor: "pointer",
            padding: "0.5rem",
            color: getColor({theme, crossBrand, inverse}),
            fontSize: getFontSize({theme, crossBrand}),
            backgroundColor: getBackgroundColor({
              theme,
              selected,
              highlighted,
              inverse,
              enabledFeatures,
            }),
            ...(inverse ? {boxShadow: "none"} : {}),
            ...(disabled ? {pointerEvents: "none", opacity: 0.6} : {}),
          },
          {disabled, highlighted, selected, crossBrand, theme}
        )
      }
      id={id}
      onClick={(e) => onClick(option, optionIndex, e)}
      onMouseMove={onMouseMove}
      role="option"
    >
      {children}
    </li>
  );
};

Option.defaultProps = defaultProps;

export default Option;
